/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Minus, Plus, Trash2, ArrowRight, ShoppingBag } from 'lucide-react';
import { Product } from '../../../types/products';
import { Button } from '../../ui/Button';

interface CartProps {
    items: { product: Product; quantity: number }[];
    onClose: () => void;
    onRemove: (id: string) => void;
    onUpdateQuantity: (id: string, delta: number) => void;
    onCheckout: () => void;
}

export const Cart: React.FC<CartProps> = ({ items, onClose, onRemove, onUpdateQuantity, onCheckout }) => {
    const subtotal = useMemo(() => items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0), [items]);
    const tax = subtotal * 0.08; // Mock tax
    const total = subtotal + tax;

    return (
        <>
            <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />
            <motion.div 
                initial={{ x: '100%' }} 
                animate={{ x: 0 }} 
                exit={{ x: '100%' }} 
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-[#15171C] shadow-2xl z-[70] flex flex-col border-l border-gray-200 dark:border-[#2D3142]"
            >
                <div className="p-6 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-[#0A0B0F]">
                    <div className="flex items-center gap-3">
                        <ShoppingBag size={20} className="text-nobel-gold" />
                        <h2 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white">Your Cart</h2>
                        <span className="px-2 py-0.5 bg-gray-200 dark:bg-white/10 rounded-full text-xs font-bold">{items.reduce((a,b)=>a+b.quantity,0)}</span>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-white/10 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4">
                            <ShoppingBag size={64} strokeWidth={1} />
                            <p>Your cart is empty.</p>
                            <Button onClick={onClose} variant="outline">Continue Shopping</Button>
                        </div>
                    ) : (
                        items.map((item) => (
                            <div key={item.product.id} className="flex gap-4">
                                <div className="w-20 h-20 bg-gray-50 dark:bg-white/5 rounded-lg flex items-center justify-center p-2 border border-gray-100 dark:border-white/5 shrink-0">
                                    <img src={item.product.image} alt={item.product.name} className="max-w-full max-h-full object-contain" />
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start">
                                            <h3 className="font-bold text-[#0A0B0F] dark:text-white text-sm line-clamp-1">{item.product.name}</h3>
                                            <button onClick={() => onRemove(item.product.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500">{item.product.category}</p>
                                    </div>
                                    <div className="flex justify-between items-end">
                                        <div className="flex items-center gap-3 bg-gray-100 dark:bg-white/5 rounded-lg p-1">
                                            <button onClick={() => onUpdateQuantity(item.product.id, -1)} className="p-1 hover:bg-white dark:hover:bg-white/10 rounded transition-colors"><Minus size={12}/></button>
                                            <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                                            <button onClick={() => onUpdateQuantity(item.product.id, 1)} className="p-1 hover:bg-white dark:hover:bg-white/10 rounded transition-colors"><Plus size={12}/></button>
                                        </div>
                                        <div className="font-mono font-bold text-[#0A0B0F] dark:text-white">
                                            ${(item.product.price * item.quantity).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {items.length > 0 && (
                    <div className="p-6 bg-gray-50 dark:bg-[#0A0B0F] border-t border-gray-100 dark:border-white/5 space-y-4">
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-gray-500">
                                <span>Subtotal</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                                <span>Taxes (Est.)</span>
                                <span>${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold text-lg text-[#0A0B0F] dark:text-white pt-2 border-t border-gray-200 dark:border-white/10">
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                        </div>
                        <Button onClick={onCheckout} className="w-full py-4 text-base shadow-lg" size="lg">
                            Proceed to Checkout <ArrowRight size={18} className="ml-2"/>
                        </Button>
                    </div>
                )}
            </motion.div>
        </>
    );
};